// src/pages/AmenitiesPage.js
import React, { useState, useEffect } from 'react';
import { makeImageAsset } from '../utils/makeImageAsset';
import foyer from '../assets/reading-towers/building/gallery/foyer-1400.jpg';
import foyerFull from '../assets/reading-towers/building/gallery/foyer-2800.jpg';
import lounge01 from '../assets/reading-towers/generated/lounge/lounge-01-1024.jpg';
import lounge01Full from '../assets/reading-towers/generated/lounge/lounge-01-2048.jpg';
import lounge02 from '../assets/reading-towers/generated/lounge/lounge-02-1024.jpg';
import lounge02Full from '../assets/reading-towers/generated/lounge/lounge-02-2048.jpg';
import lounge03 from '../assets/reading-towers/generated/lounge/lounge-03-1024.jpg';
import lounge03Full from '../assets/reading-towers/generated/lounge/lounge-03-2048.jpg';

const foyerImage = makeImageAsset({
  src: foyer,
  src2x: foyerFull,
  alt: 'Reading Tower foyer',
  srcWidth: 1400,
  src2xWidth: 2800,
});

const lounge01Image = makeImageAsset({
  src: lounge01,
  src2x: lounge01Full,
  alt: 'Reading Tower resident lounge',
  srcWidth: 1024,
  src2xWidth: 2048,
});

const lounge02Image = makeImageAsset({
  src: lounge02,
  src2x: lounge02Full,
  alt: 'Reading Tower resident lounge seating',
  srcWidth: 1024,
  src2xWidth: 2048,
});

const lounge03Image = makeImageAsset({
  src: lounge03,
  src2x: lounge03Full,
  alt: 'Reading Tower resident lounge view',
  srcWidth: 1024,
  src2xWidth: 2048,
});

const amenitiesGalleryGridData = [
  {
    type: "double",
    images: [lounge01Image, lounge02Image],
  },
  {
    type: "single",
    images: [lounge03Image],
  },
];

const AmenitiesPage = () => {
  const [zoomSrc, setZoomSrc] = useState(null);

  useEffect(() => {
    document.body.style.overflow = zoomSrc ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [zoomSrc]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setZoomSrc(null);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <div className="bg-white text-gray-700">
      {/* Section 1: Main Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-3 space-y-6">
            <p className="text-sm font-semibold uppercase tracking-wider text-teal-700">
              AMENITIES
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-1400 leading-tight">
              Practical Amenities in Downtown Reading
            </h1>
            <p className="text-lg leading-relaxed text-gray-600">
              Reading Tower brings practical everyday amenities to a central
              Downtown Reading address. Residents have access to spaces designed
              for fitness, work, packages, and gathering, with modern residences
              above Penn Street.
            </p>
            <p className="text-lg leading-relaxed text-gray-600">
              The building includes a gym, lounge, conference room, and package
              room. Inside the residences, large windows, efficient layouts, and
              updated finishes create comfortable spaces for daily life.
            </p>
          </div>
          <div className="md:col-span-2">
            <img
              src={foyerImage.src}
              srcSet={foyerImage.srcSet}
              sizes="(min-width: 768px) 50vw, 100vw"
              alt={foyerImage.alt}
              className="rounded-lg shadow-xl w-full aspect-[4/3] object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Image Grid */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-4 md:space-y-6">
          {amenitiesGalleryGridData.map((item, idx) => (
            <div key={idx}>
              {item.type === 'single' && (
                <img
                  src={item.images[0].src}
                  srcSet={item.images[0].widthSrcSet}
                  sizes="100vw"
                  alt={item.images[0].alt}
                  className="w-full h-auto md:h-[60vh] object-cover rounded-lg shadow-lg align-bottom"
                  loading="lazy"
                  decoding="async"
                  onClick={() => setZoomSrc(item.images[0].full)}
                />
              )}

              {item.type === 'double' && (
                <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                  {item.images.map((image) => (
                    <div key={image.src} className="w-full sm:w-1/2">
                      <img
                        src={image.src}
                        srcSet={image.widthSrcSet}
                        sizes="(min-width: 640px) 50vw, 100vw"
                        alt={image.alt}
                        className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-lg shadow-lg"
                        loading="lazy"
                        decoding="async"
                        onClick={() => setZoomSrc(image.full)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {zoomSrc && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setZoomSrc(null)}
        >
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="absolute top-2 right-2 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 flex items-center justify-center bg-black bg-opacity-50 rounded-full text-white z-10"
              aria-label="Close zoom"
            >
              ×
            </button>
            <img
              src={zoomSrc}
              alt="Zoomed"
              className="max-w-[90vw] max-h-[90vh] rounded-lg object-contain"
              loading="eager"
            />
          </div>
        </div>
      )}

      {/* Section 3: Featured Amenities List */}
      <section className="pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-semibold text-center text-teal-700 mb-12">
            Featured Amenities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl mx-auto text-gray-600">
            <ul className="space-y-3 list-disc list-inside">
              <li>Gym</li>
              <li>Lounge</li>
              <li>Conference Room</li>
              <li>Package Room</li>
            </ul>
            <ul className="space-y-3 list-disc list-inside">
              <li>Downtown Reading Location</li>
              <li>Nearby Restaurants And Local Businesses</li>
              <li>Nearby Public Transportation Options</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmenitiesPage;
